import {
  Avatar,
  Box,
  Button,
  ColumnDatas,
  Table,
  Tooltip,
  Typography,
} from "fds";
import { useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utilsCommon from "../../shared/utils/common";
import AdminFilter from "../../components/AdminFilter";
import { FilterData } from "../../types/adminFilter";
import useAdminLogStore from "../../redux/dispatcher/useAdminLogStore";
import { LogInfo } from "../../types/adminLog";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";
import { Workgroup } from "../../types/workgroup";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import WorkgroupPolicyTooltip from "../../pages/Workgroup/WorkgroupLog/WorkgroupPolicyTooltip";
import FilesharePolicyTooltip from "../../pages/Workgroup/WorkgroupLog/FilesharePolicyTooltip";
import { LogItem } from "../../types/files";

const WorkgroupLog: React.FC = () => {
  const { t } = useTranslation();
  const currentDate = new Date();
  const timezoneOffset = currentDate.getTimezoneOffset();
  const { adminLogStore, getWorkgroupLog, getWorkgroupLogExcel } =
    useAdminLogStore();
  const { adminWorkgroupStore, getWorkgroupList } = useAdminWorkgroupStore();

  /**** State 모음 ****/
  const initFilterData = {
    userId: "",
    workgroupId: "",
    dateFilter: "1month",
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().endOf("day").valueOf(),
    },
    order: "datedesc",
    filterWorkgroup: [0, 1, 2, 3, 4],
    filterDocument: [],
    filterWebMeeting: [],
    filterChat: [],
  } as FilterData;

  const [filterData, setFilterData] = useState<FilterData>(initFilterData);
  const [workgroupLogs, setWorkgroupLogs] = useState([] as LogInfo[]);
  const pageCount = 50;
  const [nextPage, setNextPage] = useState(-1);
  const [workgroupList, setWorkgroupList] = useState([] as Workgroup[]);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const tableRef = useRef<HTMLDivElement>(null);
  const [initDone, setInitDone] = useState(false);

  /**** UseEffect 모음 ****/
  useEffect(() => {
    getWorkgroupLogList();
    getWorkgroupList({
      orderBy: "workgroupName",
      orderByType: "ASC",
      status: -1,
      posPage: 1,
      pageRange: 1,
      rowRange: 0,
      searchDate: {
        start: 0,
        end: 0,
      },
      workgroupName: "",
      managerId: "",
      isDetail: "",
    });
    setInitDone(true);
  }, []);

  useEffect(() => {
    if (adminLogStore.workgroupLog.posPage === 1) {
      setWorkgroupLogs(adminLogStore.workgroupLog.logResults);
    } else {
      setWorkgroupLogs(
        workgroupLogs.concat(adminLogStore.workgroupLog.logResults)
      );
    }
    if (
      adminLogStore.workgroupLog.posPage * pageCount <
      adminLogStore.workgroupLog.total
    ) {
      setNextPage(adminLogStore.workgroupLog.posPage + 1);
    } else {
      setNextPage(-1);
    }
  }, [adminLogStore.workgroupLog]);

  useEffect(() => {
    if (adminWorkgroupStore.usingWorkgroup.workgroupList?.length > 0) {
      setWorkgroupList(adminWorkgroupStore.usingWorkgroup.workgroupList);
    }
  }, [adminWorkgroupStore.usingWorkgroup.workgroupList]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    initDone && getWorkgroupLogList();
  }, [filterData]);

  const getWorkgroupLogList = (page?: number) => {
    if (page !== -1) {
      getWorkgroupLog({
        posPage: page ?? 1,
        pageRange: 1,
        rowRange: pageCount,
        filterWorkgroup:
          filterData.filterWorkgroup && filterData.filterWorkgroup.length === 0
            ? [0, 1, 2, 3, 4]
            : filterData.filterWorkgroup ?? [0, 1, 2, 3, 4],
        filterDocument: [],
        filterWebMeeting: [],
        filterChat: [],
        workgroupId: filterData.workgroupId ?? "",
        order: filterData.order + "",
        userId: filterData.userId + "",
        startDate:
          filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
        endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
      });
    }
  };

  const renderProperHistoryType = (row: LogItem) => {
    return row.historyType === 0
      ? t(`log.filterWebMeeting.${row.saveType}`)
      : row.historyType === 1
      ? t(`log.filterDocument.${row.saveType}`)
      : row.historyType === 2
      ? t(`log.filterWorkgroup.${row.saveType}`)
      : t(`log.filterChat.${row.saveType}`);
  };

  function transformFileList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        if (item && item?.workgroupId && item?.workgroupName) {
          const dataItemTransformed = {
            data: [
              {
                columnText: "",
              },
              {
                columnText: (
                  <Box alignItems="center">
                    <Avatar
                      uniqueCode={item.workgroupId}
                      name={
                        item.workgroupId === "NONEWORKGROUP"
                          ? "My Box"
                          : item.workgroupName
                      }
                      shape="workgroup"
                    />
                    <Tooltip
                      message={
                        item.workgroupId === "NONEWORKGROUP"
                          ? "My Box"
                          : item.workgroupName
                      }
                    >
                      <Typography ellipsis classes="mg ml-10">
                        {item.workgroupId === "NONEWORKGROUP"
                          ? "My Box"
                          : item.workgroupName}
                      </Typography>
                    </Tooltip>
                  </Box>
                ),
              },
              {
                columnText: (
                  <Tooltip
                    message={utilsCommon.getUserNameIdTag(
                      item.userId,
                      item.userName,
                      true
                    )}
                  >
                    <Typography type="body2_rg" ellipsis>
                      {utilsCommon.getUserNameIdTag(
                        item.userId,
                        item.userName,
                        true
                      )}
                    </Typography>
                  </Tooltip>
                ),
              },
              {
                columnText: item.policyLogs ? (
                  <Tooltip
                    toolTipMaxWidth={"max-content"}
                    message={
                      item.saveType === 10 &&
                      item.policyLogs[0].permissionPolicy.policy
                        ?.fileShareLinkPolicy ? (
                        <FilesharePolicyTooltip
                          linkPolicy={
                            item.policyLogs[0].permissionPolicy.policy
                              ?.fileShareLinkPolicy
                          }
                          logDate={item.logDate}
                        />
                      ) : (
                        <WorkgroupPolicyTooltip logItem={item.policyLogs} />
                      )
                    }
                  >
                    <Typography type="body2_rg" ellipsis>
                      {renderProperHistoryType(item)}
                    </Typography>
                  </Tooltip>
                ) : item.historyType === 1 && item.desc ? (
                  <Tooltip
                    message={
                      (item.saveType === 8
                        ? t("admin.filter.fileManager") + ":"
                        : item.saveType === 3
                        ? t("log.detailLog.pastFileName")
                        : "") + item.desc
                    }
                  >
                    <Typography type="body2_rg" ellipsis>
                      {renderProperHistoryType(item)}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography type="body2_rg" ellipsis>
                    {renderProperHistoryType(item)}
                  </Typography>
                ),
              },
              {
                columnText: (
                  <Typography type="body2_rg" ellipsis>
                    {item.ip}
                  </Typography>
                ),
              },
              {
                columnText: (
                  <Typography type="body2_rg">
                    {utilsCommon.renderDateFormat(item.logDate)}
                  </Typography>
                ),
              },
            ],
            id: item.logId,
          };
          dataTransformed.push(dataItemTransformed);
        }
      });
    return dataTransformed;
  }

  return (
    <Box classes="adminWorkgroupLog" direction="column">
      <Box classes="adminWorkgroupLog-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.workgroupLog")}</Typography>
      </Box>
      <Box classes="adminWorkgroupLog-title" justifyContent="space-between">
        <AdminFilter
          initFilterData={initFilterData}
          filterData={filterData}
          setFilterData={setFilterData}
          workgroupList={workgroupList}
        />
        <Button
          type="primary"
          btnStyle="fill"
          label={t("admin.log.export")}
          onClick={() => {
            getWorkgroupLogExcel({
              posPage: 1,
              pageRange: 1,
              rowRange: pageCount,
              filterWorkgroup:
                filterData.filterWorkgroup &&
                filterData.filterWorkgroup.length === 0
                  ? [0, 1, 2, 3, 4]
                  : filterData.filterWorkgroup ?? [0, 1, 2, 3, 4],
              filterDocument: [],
              filterWebMeeting: [],
              filterChat: [],
              workgroupId: filterData.workgroupId ?? "",
              order: filterData.order + "",
              userId: filterData.userId + "",
              startDate:
                filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
              endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
            });
          }}
          state={workgroupLogs.length > 0 ? "default" : "disable"}
        />
      </Box>

      <Box ref={tableRef} classes="mg mt-12" direction="column">
        <Table
          width={"100%"}
          height={(workgroupLogs.length === 0 ? 0 : height) + "px"}
          header={[
            {
              headerText: "",
              width: "24px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.workgroupName"),
              width: "calc(100% - 1000px)",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.user"),
              width: "300px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.filter.type"),
              width: "300px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.ip"),
              width: "150px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.lastUpdateDate"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
          ]}
          data={transformFileList(workgroupLogs)}
          totalCount={workgroupLogs.length}
          endReached={() => {
            getWorkgroupLogList(nextPage);
          }}
          columnHeight="md"
        />
        {workgroupLogs.length === 0 && <EmptyFilterPage />}
      </Box>
    </Box>
  );
};

export default WorkgroupLog;
